import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import {
  MISSING,
  authorInitials,
  authorLabel,
  playerHandleSuffix,
  socialTimeLabel,
} from "@bw/shared/components";
import type { MediaEmbed, PostResponse } from "@bw/shared/types/generated";
import { renderMarkdown } from "@bw/shared/utils";
import { useRef, useState, type ReactNode } from "react";

export interface BusinessCommunityPostCardProps {
  post: PostResponse;
  actions?: ReactNode;
}

function safeAvatarUrl(url: string | null | undefined): string | undefined {
  return url?.startsWith("https://") ? url : undefined;
}

function PostHeader({
  post,
  compact = false,
}: {
  post: PostResponse;
  compact?: boolean;
}) {
  const name = authorLabel(post.author_display_name, post.author_id);
  const avatarUrl = safeAvatarUrl(post.author_avatar_url);
  const time = socialTimeLabel(post.created_at);

  return (
    <CardHeader
      avatar={
        <Avatar
          alt=""
          aria-hidden
          src={avatarUrl}
          sx={{
            fontSize: compact ? "0.75rem" : "0.875rem",
            height: compact ? 32 : 40,
            width: compact ? 32 : 40,
          }}
        >
          {avatarUrl ? null : authorInitials(post.author_display_name)}
        </Avatar>
      }
      subheader={
        time || post.edited_at ? (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            {time ? (
              <Typography
                component="time"
                color="text.secondary"
                dateTime={post.created_at ?? undefined}
                variant="caption"
              >
                {time}
              </Typography>
            ) : null}
            {post.edited_at ? (
              <Typography
                aria-label="Edited"
                color="text.secondary"
                component="span"
                variant="caption"
              >
                · Edited
              </Typography>
            ) : null}
          </Stack>
        ) : null
      }
      sx={compact ? { p: 0 } : { p: 2 }}
      title={
        <Typography component="span" variant={compact ? "body2" : "subtitle2"}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {name}
          </Box>
          {!compact && name !== MISSING ? (
            <Typography
              component="span"
              color="text.secondary"
              variant="caption"
            >
              {` ${playerHandleSuffix(post.author_id)}`}
            </Typography>
          ) : null}
        </Typography>
      }
    />
  );
}

function MarkdownContent({ children }: { children: string }) {
  return (
    <Box
      sx={{
        overflowWrap: "anywhere",
        "& > :first-of-type": { mt: 0 },
        "& > :last-child": { mb: 0 },
        "& p": { my: 1 },
        "& ul, & ol": { my: 1, pl: 3 },
        "& a": { color: "primary.main" },
        "& code": {
          bgcolor: "action.hover",
          borderRadius: 0.5,
          px: 0.5,
          py: 0.25,
        },
      }}
    >
      {renderMarkdown(children)}
    </Box>
  );
}

function EmbeddedPost({ post }: { post: PostResponse }) {
  if (post.is_deleted) {
    return (
      <Box
        aria-label="Reposted post"
        component="blockquote"
        sx={{
          bgcolor: "action.hover",
          borderLeft: 3,
          borderColor: "divider",
          m: 0,
          mx: 2,
          mb: 2,
          p: 2,
        }}
      >
        <Typography color="text.secondary" variant="body2">
          This post is no longer available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      aria-label="Reposted post"
      component="blockquote"
      sx={{
        bgcolor: "action.hover",
        borderLeft: 3,
        borderColor: "primary.main",
        m: 0,
        mx: 2,
        mb: 2,
        p: 2,
      }}
    >
      <Typography color="text.secondary" component="div" variant="caption">
        Reposted from {authorLabel(post.author_display_name, post.author_id)}
      </Typography>
      <Box sx={{ mt: 1 }}>
        <PostHeader compact post={post} />
      </Box>
      {post.content ? (
        <Box sx={{ mt: 1 }}>
          <MarkdownContent>{post.content}</MarkdownContent>
        </Box>
      ) : null}
      <BusinessCommunityPostMedia inset media={post.media ?? []} />
    </Box>
  );
}

/** Native-MUI Business composition for a server-backed community post. */
export function BusinessCommunityPostCard({
  post,
  actions,
}: BusinessCommunityPostCardProps) {
  const original = post.reposted_post ?? null;

  return (
    <Card component="article" variant="outlined" sx={{ overflow: "hidden" }}>
      {original ? (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ alignItems: "center", px: 2, pt: 1.5 }}
        >
          <RepeatRoundedIcon color="action" fontSize="small" />
          <Typography color="text.secondary" variant="caption">
            {authorLabel(post.author_display_name, post.author_id)} reposted
          </Typography>
        </Stack>
      ) : null}
      <PostHeader post={post} />
      {post.content ? (
        <CardContent sx={{ px: 2, pt: 0 }}>
          <MarkdownContent>{post.content}</MarkdownContent>
        </CardContent>
      ) : null}
      <BusinessCommunityPostMedia media={post.media ?? []} />
      {original ? <EmbeddedPost post={original} /> : null}
      {actions ? (
        <CardActions sx={{ borderTop: 1, borderColor: "divider", px: 2 }}>
          {actions}
        </CardActions>
      ) : null}
    </Card>
  );
}

export function BusinessCommunityPostMedia({
  media,
  inset = false,
}: {
  media: MediaEmbed[];
  inset?: boolean;
}) {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const renderable = media.filter(
    (entry) =>
      entry.media_type === "image" ||
      (entry.media_type === "youtube" && Boolean(entry.thumbnail_url)),
  );

  if (renderable.length === 0) return null;

  const visible = renderable.slice(0, 4);
  const remaining = renderable.length - visible.length;

  return (
    <>
      <ImageList
        aria-label="Post attachments"
        cols={2}
        gap={4}
        rowHeight={160}
        sx={{ m: 0, mx: inset ? 0 : 2, mb: inset ? 0 : 2, mt: inset ? 1.5 : 0 }}
        variant="quilted"
      >
        {visible.map((entry, index) => {
          const single = visible.length === 1;
          const tallLead = visible.length === 3 && index === 0;
          return (
            <ImageListItem
              cols={single ? 2 : 1}
              key={`${entry.url}-${index}`}
              rows={single || tallLead ? 2 : 1}
            >
              <ButtonBase
                aria-haspopup="dialog"
                aria-label={
                  entry.media_type === "youtube"
                    ? "Play YouTube video"
                    : `View image ${index + 1} of ${renderable.length}`
                }
                onClick={(event) => {
                  triggerRef.current = event.currentTarget;
                  setViewerIndex(index);
                }}
                sx={{ height: "100%", position: "relative", width: "100%" }}
                type="button"
              >
                <Box
                  alt=""
                  aria-hidden
                  component="img"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  src={
                    entry.media_type === "youtube"
                      ? (entry.thumbnail_url ?? "")
                      : entry.url
                  }
                  sx={{ height: "100%", objectFit: "cover", width: "100%" }}
                />
                {entry.media_type === "youtube" ? (
                  <Avatar
                    aria-hidden
                    sx={{
                      bgcolor: (theme) =>
                        alpha(theme.palette.common.black, 0.72),
                      position: "absolute",
                    }}
                  >
                    <PlayArrowRoundedIcon />
                  </Avatar>
                ) : null}
                {remaining > 0 && index === visible.length - 1 ? (
                  <Box
                    aria-hidden
                    sx={{
                      alignItems: "center",
                      bgcolor: (theme) =>
                        alpha(theme.palette.common.black, 0.6),
                      color: "common.white",
                      display: "flex",
                      fontSize: "1.75rem",
                      fontWeight: 700,
                      inset: 0,
                      justifyContent: "center",
                      position: "absolute",
                    }}
                  >
                    +{remaining}
                  </Box>
                ) : null}
              </ButtonBase>
            </ImageListItem>
          );
        })}
      </ImageList>
      <PostMediaDialog
        index={viewerIndex}
        media={renderable}
        onClose={() => {
          setViewerIndex(null);
          const trigger = triggerRef.current;
          triggerRef.current = null;
          requestAnimationFrame(() => {
            if (trigger?.isConnected) trigger.focus();
          });
        }}
        onNavigate={setViewerIndex}
      />
    </>
  );
}

function PostMediaDialog({
  media,
  index,
  onNavigate,
  onClose,
}: {
  media: MediaEmbed[];
  index: number | null;
  onNavigate: (index: number) => void;
  onClose: () => void;
}) {
  const entry = index === null ? null : media[index];
  const youtubeUrl =
    entry?.media_type === "youtube" && entry.youtube_id
      ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(entry.youtube_id)}`
      : null;

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      open={index !== null}
      slotProps={{
        paper: {
          "aria-label": "Image viewer",
          sx: { bgcolor: "common.black", minHeight: "70vh" },
        },
      }}
    >
      <DialogContent sx={{ alignItems: "center", display: "flex", p: 0 }}>
        <IconButton
          aria-label="Close"
          autoFocus
          onClick={onClose}
          sx={{
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
            color: "common.white",
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 2,
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        {entry?.media_type === "image" ? (
          <Box
            alt="Post image attachment"
            component="img"
            referrerPolicy="no-referrer"
            src={entry.url}
            sx={{ height: "70vh", objectFit: "contain", width: "100%" }}
          />
        ) : youtubeUrl ? (
          <Box
            allow="encrypted-media; picture-in-picture"
            allowFullScreen
            component="iframe"
            sandbox="allow-scripts allow-same-origin allow-presentation"
            src={youtubeUrl}
            sx={{ border: 0, height: "70vh", width: "100%" }}
            title="YouTube video player"
          />
        ) : null}
        {index !== null && media.length > 1 ? (
          <>
            <IconButton
              aria-label="Previous image"
              onClick={() =>
                onNavigate((index - 1 + media.length) % media.length)
              }
              sx={{
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
                color: "common.white",
                left: 8,
                position: "absolute",
                top: "50%",
              }}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              aria-label="Next image"
              onClick={() => onNavigate((index + 1) % media.length)}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
                color: "common.white",
                position: "absolute",
                right: 8,
                top: "50%",
              }}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
